import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { BaseServices } from '../../kuba.services';
import { ManualView } from '../models';
import { ManualService } from './manual.services';

const CACHE_KEY = 'manualNavigation';

/**
 * The manual list the Manuals page shows, kept in session storage for the hover
 * sub-navigation. The Manuals page stores the list whenever it loads or
 * refreshes it; before the first visit, the first hover fetches it once.
 */
@Injectable({ providedIn: 'root' })
export class ManualNavigationService {
  private readonly manualService = inject(ManualService);
  private pending?: Observable<ManualView[]>;

  /** The current context's manuals, from session storage when available. */
  manuals(): Observable<ManualView[]> {
    const cached = this.read();
    if (cached) {
      return of(cached);
    }
    if (!this.pending) {
      const request = this.request();
      if (!request) {
        return of([]);
      }
      this.pending = request.pipe(
        map((result: any) => (Array.isArray(result) ? result : [])),
        tap(list => this.store(list)),
        finalize(() => (this.pending = undefined)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.pending;
  }

  /**
   * Replaces the stored list for the current context.
   * @param manuals The list the Manuals page shows by default.
   */
  store(manuals: ManualView[] | null | undefined): void {
    try {
      sessionStorage.setItem(this.key(), JSON.stringify(manuals ?? []));
    } catch {
      // Storage full or unavailable: the next hover fetches the list again.
    }
  }

  private read(): ManualView[] | null {
    try {
      const manuals = JSON.parse(sessionStorage.getItem(this.key()) ?? 'null');
      return Array.isArray(manuals) ? manuals : null;
    } catch {
      return null;
    }
  }

  /** Session storage key; in R mode the role is Editor and the business is the entered one. */
  private key(): string {
    const role = BaseServices.UserRole;
    switch (role) {
      case 'Admin':
        return `${CACHE_KEY}:${role}:${BaseServices.ApplicationId}`;
      case 'Portal':
        return `${CACHE_KEY}:${role}:${BaseServices.PortalId}`;
      default:
        return `${CACHE_KEY}:${role}:${BaseServices.BusinessId}`;
    }
  }

  /** The same request per role as `ManualListResolver`. */
  private request(): Observable<any> | null {
    switch (BaseServices.UserRole) {
      case 'Admin':
        return this.manualService.getCommonManuallist(
          BaseServices.ApplicationId
        );
      case 'Portal':
        return this.manualService.getCommonManuallistByPortalLogin(
          +BaseServices.PortalId
        );
      case 'Editor':
      case 'User':
      case 'Guest':
        return this.manualService.getCommonManuallistByBusinessLogin(
          BaseServices.BusinessId
        );
      default:
        return null;
    }
  }
}
